import {Dispatch, MutableRefObject, SetStateAction} from "react"
import {
    I18nIndividual as GedcomIndividual,
    IndividualBindings as DeserializedIndividual
} from "../model/gedcom-individual"
import {FamilyBindings as DeserializedFamily, I18nFamily as GedcomFamily} from "../model/gedcom-family"
import * as Sentry from "@sentry/react"


export function storeDeltaState(
    lastIndividualId: MutableRefObject<number>,
    individualsData: GedcomIndividual[],
    lastSavedIndividuals: MutableRefObject<GedcomIndividual[]>,
    lastFamilyId: MutableRefObject<number>,
    familiesData: GedcomFamily[],
    lastSavedFamilies: MutableRefObject<GedcomFamily[]>
) {
    try {
        // Skip if the row is the placeholder
        if (individualsData.length === 1 && individualsData[0].pristine) return
        // Trigger persistence only if row count increases
        const prev = lastSavedIndividuals.current.length + lastSavedFamilies.current.length
        const current = individualsData.length + familiesData.length
        if (current <= prev) return
        // Persist only state delta
        const touchedIndividualsData = individualsData.filter(i => !i.pristine)
        const touchedFamiliesData = familiesData.filter(f => !f.pristine)
        if (touchedIndividualsData.length || touchedFamiliesData.length) {
            const storedData = JSON.parse(sessionStorage.getItem("creatorData") || localStorage.getItem("creatorData") || "{}")
            // keep all existing rows that were not touched, but replace updated rows
            const mergedIndividuals = [
                ...(storedData.savedIndividualsData || []).filter((saved: any) => !touchedIndividualsData.some(updated => updated.id === saved.id)),
                ...touchedIndividualsData
            ]
            const mergedFamilies = [
                ...(storedData.savedFamiliesData || []).filter((saved: any) => !touchedFamiliesData.some(updated => updated.id === saved.id)),
                ...touchedFamiliesData
            ]
            sessionStorage.setItem("creatorData", JSON.stringify({
                ...storedData,
                savedIndividualsData: mergedIndividuals,
                savedFamiliesData: mergedFamilies,
                savedLastIndividualId: lastIndividualId.current,
                savedLastFamilyId: lastFamilyId.current
            }))
            lastSavedIndividuals.current = [...individualsData]
            lastSavedFamilies.current = [...familiesData]
        }
    } catch (e: any) {
        console.error("Failed to save creator data to local storage:", e.message)
        Sentry.captureException(e, { extra: {
            context: "Failed to save creator data to local storage",
            storageSize: `${getStorageSize(sessionStorage)}MB`
        }})
    }
}

export function storeState(
    storage: Storage,
    recentIndividuals: MutableRefObject<GedcomIndividual[]>,
    lastIndividualId: MutableRefObject<number>,
    recentFamilies: MutableRefObject<GedcomFamily[]>,
    lastFamilyId: MutableRefObject<number>,
) {
    try {
        const storedData = JSON.parse(sessionStorage.getItem("creatorData") || localStorage.getItem("creatorData") || "{}")
        storage.setItem("creatorData", JSON.stringify({...storedData,
            savedIndividualsData: recentIndividuals.current,
            savedFamiliesData: recentFamilies.current,
            savedLastIndividualId: lastIndividualId.current,
            savedLastFamilyId: lastFamilyId.current
        }))
    } catch (e) {
        console.error("Failed to save creator data to local storage:", e)
        Sentry.captureException(e, { extra: {
            context: "Failed to save creator data to local storage",
            storageSize: `${getStorageSize(storage)}MB`
        }})
    }
}

export function restoreState(
    lastIndividualId: MutableRefObject<number>,
    setIndividualsData: Dispatch<SetStateAction<GedcomIndividual[]>>,
    lastFamilyId: MutableRefObject<number>,
    setFamiliesData: Dispatch<SetStateAction<GedcomFamily[]>>
) {
    try {
        const restoredData = JSON.parse(localStorage.getItem("creatorData") || sessionStorage.getItem("creatorData") || "{}")
        // Mark restored rows as pristine
        if (restoredData.savedIndividualsData?.length) {
            setIndividualsData(restoredData.savedIndividualsData.map((restored: any) => {
                const restoredIndividual = new DeserializedIndividual(restored)
                hydrateSetFields(restoredIndividual, ["languages", "familySpouses", "_familySpouses"])
                restoredIndividual.restored = true
                return restoredIndividual
            }))
        }
        if (restoredData.savedFamiliesData?.length) {
            setFamiliesData(restoredData.savedFamiliesData.map((restored: any) => {
                const restoredFamily = new DeserializedFamily(restored)
                hydrateSetFields(restoredFamily, ["children", "_children"])
                restoredFamily.restored = true
                return restoredFamily
            }))
        }
        if (restoredData.savedLastIndividualId) lastIndividualId.current = restoredData.savedLastIndividualId
        if (restoredData.savedLastFamilyId) lastFamilyId.current = restoredData.savedLastFamilyId
    } catch (e) {
        console.error("Failed to parse saved creator data:", e)
        Sentry.captureException(e, { extra: { context: "Failed to parse saved creator data" }})
    }
}

/**
 * Storage primitives only accept strings, so we need to handle manually.
 * Otherwise, deserialization will override the sets with sheer JSON arrays
 */
function hydrateSetFields<T>(row: T, setKeys: (keyof T)[]): T {
    for (const key of setKeys) {
        const field = row[key]
        if (!(field instanceof Set))
            row[key] = new Set(Array.isArray(field) ? field : Object.keys(field || {})) as any
    }
    return row
}

export function getStorageSize(storage: Storage) {
    let total = 0
    for (let key in storage) {
        if (storage.hasOwnProperty(key)) {
            const value = storage.getItem(key)
            total += key.length + (value ? value.length : 0)
        }
    }
    return total * 2 / 1024 / 1024
}